/**
 * <pdp-notify-me> — themed AMP Back in Stock signup popup.
 */

import { Delegate } from '@theme/delegate';
import { FormValidator } from '@theme/form-validator';
import { NewsletterHandler } from '@theme/newsletter-handler';
import { globalPopup } from '@theme/popup';

const AMP_REQUEST_TIMEOUT_MS = 15000;
const NEWSLETTER_REQUEST_TIMEOUT_MS = 15000;
const LOCAL_STOREFRONT_HOSTS = new Set(['127.0.0.1', 'localhost', '0.0.0.0']);

class PdpNotifyMe extends HTMLElement {
  static #openDelegate = null;

  #delegate = null;
  #validator = null;
  #strings = {};
  #popup = null;
  #form = null;
  #emailInput = null;
  #checkboxInputs = [];
  #newsletterOptIn = null;
  #newsletterHandler = null;
  #newsletterConfig = { listId: '', publicKey: '' };
  #isSubmitting = false;
  #submissionId = 0;
  #variant = null;
  #sizeLabel = '';
  #touchedFields = new Set();
  #favoritesContext = false;
  #popupVisibilityObserver = null;
  #favoritesOverlayGuard = null;
  #favoritesEscapeGuard = null;

  #popupQuery(selector) {
    return this.#popup?.querySelector(selector) ?? null;
  }

  connectedCallback() {
    const stringsEl = this.querySelector('[data-ref="strings"]');
    this.#strings = JSON.parse(stringsEl?.textContent || '{}');

    this.#popup = document.getElementById(this.dataset.popupId);
    this.#favoritesContext = Boolean(this.closest('favorites-drawer'));

    if (this.#popup) {
      // Popup is moved to <body>, so CSS can't use `favorites-drawer …` ancestors.
      if (this.#favoritesContext) {
        this.#popup.setAttribute('data-notify-context', 'favorites');
        this.#observeFavoritesPopup();
      } else {
        this.#popup.removeAttribute('data-notify-context');
      }

      if (this.#popup.parentElement !== document.body) {
        document.body.appendChild(this.#popup);
      }
    }

    this.#form = this.#popupQuery('[data-ref="form"]');
    this.#emailInput = this.#popupQuery('[data-ref="email-input"]');
    this.#checkboxInputs = [
      ...(this.#popup?.querySelectorAll(
        '[data-ref="checkboxes"] .form-checkbox input[type="checkbox"]',
      ) ?? []),
    ];
    this.#newsletterOptIn = this.#resolveNewsletterOptIn();
    this.#initNewsletterHandler();

    this.#delegate = new Delegate(this);
    PdpNotifyMe.#bindOpenTrigger();
    this.#form?.addEventListener('submit', this.#onSubmit);
    this.#form?.addEventListener('input', this.#onFormInteraction);
    this.#form?.addEventListener('change', this.#onFormInteraction);
    this.#form?.addEventListener('focusout', this.#onFormBlur);

    document.addEventListener('variant:changed', this.#onVariantChanged);

    if (this.#form) {
      this.#validator = new FormValidator(this.#form, {
        fieldSelector: '.field, .form-checkbox',
        showErrorsOnSubmitOnly: true,
        validateOnInput: false,
        validateOnBlur: false,
      });

      this.#checkboxInputs.forEach((input) => {
        if (!input.hasAttribute('required')) return;

        const message = this.#strings.checkboxRequired;

        this.#validator.addValidator(
          input.id,
          (_value, field) => (field.checked ? true : message),
          message,
        );
      });
    }

    this.#syncVariantContext();
    this.closest('product-component')?.dispatchEvent(
      new CustomEvent('notify-me:ready', { bubbles: true }),
    );
  }

  disconnectedCallback() {
    this.#submissionId += 1;
    if (this.#popup) globalPopup.closePopup(this.#popup);
    document.removeEventListener('variant:changed', this.#onVariantChanged);
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.#form?.removeEventListener('input', this.#onFormInteraction);
    this.#form?.removeEventListener('change', this.#onFormInteraction);
    this.#form?.removeEventListener('focusout', this.#onFormBlur);
    this.#delegate?.off();
    this.#popupVisibilityObserver?.disconnect();
    this.#popupVisibilityObserver = null;
    this.#newsletterHandler?.abort();
    this.#newsletterHandler = null;
    this.#unbindFavoritesGuards();
    this.#restoreFavoritesDrawerOverlay();
    this.#popup?.remove();
    this.#popup = null;
  }

  #observeFavoritesPopup() {
    if (!this.#popup || this.#popupVisibilityObserver) return;

    this.#popupVisibilityObserver = new MutationObserver(() => {
      const isOpen = this.#popup?.getAttribute('aria-hidden') === 'false';
      if (isOpen) {
        this.#bindFavoritesGuards();
        return;
      }

      this.#unbindFavoritesGuards();
      this.#restoreFavoritesDrawerOverlay();
    });

    this.#popupVisibilityObserver.observe(this.#popup, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  #bindFavoritesGuards() {
    if (this.#favoritesOverlayGuard) return;

    const overlay = document.querySelector('.page-overlay');
    this.#favoritesOverlayGuard = (event) => {
      event.stopImmediatePropagation();
      event.preventDefault();
      if (this.#popup) globalPopup.closePopup(this.#popup);
    };
    overlay?.addEventListener('click', this.#favoritesOverlayGuard, true);

    this.#favoritesEscapeGuard = (event) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      if (this.#popup) globalPopup.closePopup(this.#popup);
    };
    document.addEventListener('keydown', this.#favoritesEscapeGuard, true);
  }

  #unbindFavoritesGuards() {
    if (this.#favoritesOverlayGuard) {
      document
        .querySelector('.page-overlay')
        ?.removeEventListener('click', this.#favoritesOverlayGuard, true);
      this.#favoritesOverlayGuard = null;
    }

    if (this.#favoritesEscapeGuard) {
      document.removeEventListener('keydown', this.#favoritesEscapeGuard, true);
      this.#favoritesEscapeGuard = null;
    }
  }

  #restoreFavoritesDrawerOverlay() {
    const favorites = document.getElementById('favorites');
    if (!favorites?.classList.contains('is-open')) return;

    const overlay = document.querySelector('.page-overlay');
    if (!overlay) return;

    overlay.classList.add('is-visible', 'is-drawer-overlay');
  }

  static #bindOpenTrigger() {
    if (PdpNotifyMe.#openDelegate) return;

    PdpNotifyMe.#openDelegate = new Delegate(document.body);
    PdpNotifyMe.#openDelegate.on('click', '[data-action="open-notify-me"]', (event) => {
      const trigger = event.target.closest('[data-action="open-notify-me"]');
      if (!trigger) return;

      const productComponent = trigger.closest('product-component');
      const favoritesDrawer = trigger.closest('favorites-drawer');
      const notifyMe =
        productComponent?.querySelector('pdp-notify-me') ??
        favoritesDrawer?.querySelector('pdp-notify-me');

      if (!notifyMe) return;

      event.preventDefault();

      if (favoritesDrawer) {
        let productData = null;
        try {
          productData = JSON.parse(trigger.dataset.productData || 'null');
        } catch {
          productData = null;
        }

        notifyMe.open({
          variantId: trigger.dataset.variantId,
          productData,
          productTitle: trigger.dataset.productTitle,
          sizeLabel: trigger.dataset.sizeLabel,
        });
        return;
      }

      notifyMe.open();
    });
  }

  open(context) {
    if (!this.#popup) return;

    this.#resetToForm();

    if (context?.variantId) {
      this.#applyExternalContext(context);
    } else {
      this.#syncVariantContext();
    }

    globalPopup.openPopup(this.#popup);
  }

  #applyExternalContext({ variantId, productData, productTitle, sizeLabel }) {
    const normalizedId = String(variantId ?? '').trim();
    this.#variant = /^\d+$/.test(normalizedId) ? { id: Number(normalizedId) } : null;

    if (productData?.id) {
      this.dataset.productId = String(productData.id);
    }

    if (productTitle || productData?.title) {
      this.dataset.productTitle = productTitle || productData.title;
    }

    const selectedVariant = productData?.variants?.find(
      (variant) => String(variant.id) === normalizedId,
    );
    const fallbackSize = selectedVariant?.title === 'Default Title' ? '' : selectedVariant?.title || '';
    this.#sizeLabel = sizeLabel?.trim() || fallbackSize;
    this.#updateIntroText();
  }

  #onFormInteraction = () => {
    this.#displayErrors();
  };

  #onFormBlur = (event) => {
    const field = this.#resolveFormField(event.target);
    if (!field) return;

    const nextField = this.#resolveFormField(event.relatedTarget);
    if (nextField === field) return;

    this.#touchedFields.add(this.#getFieldKey(field));
    this.#displayErrors();
  };

  #resolveFormField(element) {
    if (!element || !this.#form?.contains(element)) return null;

    if (element.matches('input, textarea, select')) return element;

    const label = element.closest('.form-checkbox__label');
    if (!label) return null;

    return label.closest('.form-checkbox')?.querySelector('input[type="checkbox"]') ?? null;
  }

  #getFieldKey(field) {
    return field.id || field.name;
  }

  #shouldShowFieldError(field) {
    return this.#validator?.hasSubmittedOnce || this.#touchedFields.has(this.#getFieldKey(field));
  }

  #onSubmit = async (event) => {
    event.preventDefault();

    if (this.#isSubmitting || !this.#validator) return;

    this.#validator.markSubmitted();

    if (!this.#isFormComplete()) {
      this.#displayErrors();
      return;
    }

    this.#clearErrors();

    const variantId = this.#getVariantId();
    const productId = this.#getProductId();
    if (!variantId || !productId) {
      this.#logAmpError('missing-product-context', { productId, variantId });
      this.#showFormError(this.#strings.submitError);
      return;
    }

    this.#isSubmitting = true;
    this.#setSubmitting(true);
    const submissionId = ++this.#submissionId;

    try {
      const email = this.#emailInput.value.trim();
      await this.#subscribeToAmp(email, variantId, productId);
      if (!this.#isCurrentSubmission(submissionId)) return;

      if (this.#newsletterOptIn?.checked) {
        await this.#subscribeToNewsletter(email);
      }
      if (!this.#isCurrentSubmission(submissionId)) return;

      this.#showSuccess(email);
    } catch (error) {
      if (!this.#isCurrentSubmission(submissionId)) return;

      if (!error?.ampLogged) {
        this.#logAmpError('submission-failed', { error, productId, variantId });
      }
      this.#showFormError(this.#resolveSubmitError(error));
    } finally {
      if (this.#isCurrentSubmission(submissionId)) {
        this.#isSubmitting = false;
        this.#setSubmitting(false);
      }
    }
  };

  #resolveNewsletterOptIn() {
    const explicitOptIn = this.#checkboxInputs.find((input) =>
      input.hasAttribute('data-newsletter-opt-in'),
    );
    if (explicitOptIn) return explicitOptIn;

    // Migration fallback for existing product templates: their sole optional
    // checkbox is "Join mailing list" and predates the purpose setting.
    const optionalCheckboxes = this.#checkboxInputs.filter((input) => !input.required);
    return optionalCheckboxes.length === 1 ? optionalCheckboxes[0] : null;
  }

  #initNewsletterHandler() {
    let settings = {};

    try {
      settings = JSON.parse(this.dataset.newsletterSettings || '{}');
    } catch (error) {
      console.error('[Klaviyo Newsletter] Invalid Notify me configuration', { error });
      return;
    }

    this.#newsletterConfig = {
      listId: settings.listId?.trim() ?? '',
      publicKey: settings.publicApiKey?.trim() ?? '',
    };

    if (!this.#newsletterConfig.listId || !this.#newsletterConfig.publicKey) return;

    this.#newsletterHandler = new NewsletterHandler({
      ...this.#newsletterConfig,
      // The popup presents its own partial-success state after AMP succeeds.
      onError: () => {},
    });
  }

  async #subscribeToNewsletter(email) {
    if (!this.#newsletterHandler) {
      console.error('[Klaviyo Newsletter] Notify me opt-in is not configured', {
        hasListId: Boolean(this.#newsletterConfig.listId),
        hasPublicKey: Boolean(this.#newsletterConfig.publicKey),
      });
      return;
    }

    let timeoutId = null;

    try {
      const request = this.#newsletterHandler.subscribe(email);
      const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          this.#newsletterHandler?.abort();
          reject(new Error('newsletter_request_timeout'));
        }, NEWSLETTER_REQUEST_TIMEOUT_MS);
      });

      await Promise.race([request, timeout]);
    } catch (error) {
      if (error?.message === 'DUPLICATE_EMAIL') return;

      console.error('[Klaviyo Newsletter] Notify me opt-in failed', {
        exception: {
          name: error?.name ?? null,
          message: error?.message ?? String(error),
        },
      });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  #isCurrentSubmission(submissionId) {
    return this.isConnected && submissionId === this.#submissionId;
  }

  #subscribeToAmp(email, variantId, productId) {
    return new Promise((resolve, reject) => {
      if (LOCAL_STOREFRONT_HOSTS.has(window.location.hostname)) {
        const error = new Error('amp_requires_shopify_origin');
        error.ampLogged = true;
        this.#logAmpError('unsupported-local-origin', { error, productId, variantId });
        reject(error);
        return;
      }

      if (typeof window.BIS?.create !== 'function') {
        const error = new Error('amp_unavailable');
        error.ampLogged = true;
        this.#logAmpError('api-unavailable', { error, productId, variantId });
        reject(error);
        return;
      }

      let timeoutId = null;

      try {
        const request = window.BIS.create(email, Number(variantId), Number(productId));
        if (typeof request?.then !== 'function') {
          const error = new Error('submit_failed');
          error.ampLogged = true;
          this.#logAmpError('invalid-api-return', { error, productId, variantId });
          reject(error);
          return;
        }

        let settled = false;
        timeoutId = setTimeout(() => {
          if (settled) return;

          settled = true;
          const error = new Error('amp_request_timeout');
          error.ampLogged = true;
          this.#logAmpError('request-timeout', { error, productId, variantId });
          reject(error);
        }, AMP_REQUEST_TIMEOUT_MS);

        request.then((response) => {
          if (settled) return;

          settled = true;
          clearTimeout(timeoutId);

          if (response?.status === 'OK') {
            resolve(response);
            return;
          }

          const error = new Error('submit_failed');
          error.ampLogged = true;
          error.userMessage = Object.values(response?.errors ?? {})
            .flat()
            .filter(Boolean)
            .join(' ');
          this.#logAmpError('server-rejected', {
            error,
            productId,
            response,
            variantId,
          });
          reject(error);
        });
      } catch (caughtError) {
        if (timeoutId) clearTimeout(timeoutId);

        const error = new Error('submit_failed');
        error.ampLogged = true;
        this.#logAmpError('api-threw', {
          error: caughtError,
          productId,
          variantId,
        });
        reject(error);
      }
    });
  }

  #logAmpError(stage, { error, productId, response, variantId } = {}) {
    console.error('[AMP Back in Stock] Notify me submission failed', {
      stage,
      productId: productId || this.#getProductId() || null,
      variantId: variantId || this.#getVariantId() || null,
      bisLoaded: Boolean(window.BIS),
      bisCreateAvailable: typeof window.BIS?.create === 'function',
      origin: window.location.origin,
      response: response
        ? {
            status: response.status ?? null,
            message: response.message ?? response.msg ?? null,
            errors: response.errors ?? null,
          }
        : null,
      exception: error
        ? {
            name: error.name ?? null,
            message: error.message ?? String(error),
          }
        : null,
    });
  }

  #resolveSubmitError(error) {
    if (error?.userMessage) return error.userMessage;
    return this.#strings.submitError;
  }

  #onVariantChanged = (event) => {
    const productComponent = this.closest('product-component');
    if (!productComponent?.contains(event.target)) return;

    this.#variant = event.detail?.variant ?? null;
    this.#sizeLabel = this.#resolveSizeLabel(this.#variant);
    this.#updateIntroText();
    this.#updateSuccessText(this.#emailInput?.value.trim() ?? '');
  };

  #syncVariantContext() {
    const productComponent = this.closest('product-component');
    const variantInput = productComponent?.querySelector('[data-ref="variant-id"]');
    const variantSelects = productComponent?.querySelector('variant-selects');
    const variants = JSON.parse(
      variantSelects?.querySelector('[data-ref="variants-json"]')?.textContent || '[]',
    );

    if (variantInput?.value) {
      this.#variant = variants.find((item) => String(item.id) === variantInput.value) ?? null;
    } else {
      this.#variant = null;
    }

    this.#sizeLabel = this.#resolveSizeLabel(this.#variant);
    this.#updateIntroText();
  }

  #resolveSizeLabel(variant) {
    if (!variant?.options?.length) return '';

    const variantSelects = this.closest('product-component')?.querySelector('variant-selects');
    const optionGroups = [
      ...(variantSelects?.querySelectorAll('.pdp-variant-option') ?? []),
    ];

    const getOptionLabel = (fieldset) => {
      const customTitle = fieldset.querySelector('.pdp-variant-option__title')?.textContent?.trim();
      const shopifyName = fieldset.dataset.optionName?.trim();
      return (customTitle || shopifyName || '').toLowerCase();
    };

    const isLengthOption = (label) =>
      /^(length|inseam|längd|lang)$/i.test(label) || /\blength\b/i.test(label);

    const isSizeOption = (label) =>
      /^size$/i.test(label) || (/\bsize\b/i.test(label) && !isLengthOption(label));

    for (const fieldset of optionGroups) {
      const position = Number(fieldset.dataset.optionPosition) - 1;
      const label = getOptionLabel(fieldset);

      if (isSizeOption(label) && variant.options[position]) {
        return variant.options[position];
      }
    }

    for (const fieldset of optionGroups) {
      const position = Number(fieldset.dataset.optionPosition) - 1;
      const label = getOptionLabel(fieldset);

      if (isLengthOption(label) || fieldset.hasAttribute('data-size-guide-option')) continue;
      if (variant.options[position]) return variant.options[position];
    }

    return variant.options[0] ?? '';
  }

  #getVariantId() {
    const variantId = this.#variant?.id ?? this.#readVariantIdFromInput();
    if (!variantId) return '';

    const normalized = String(variantId).trim();
    return /^\d+$/.test(normalized) ? normalized : '';
  }

  #getProductId() {
    const normalized = String(this.dataset.productId ?? '').trim();
    return /^\d+$/.test(normalized) ? normalized : '';
  }

  #readVariantIdFromInput() {
    const variantInput = this.closest('product-component')?.querySelector('[data-ref="variant-id"]');
    return variantInput?.value?.trim() ?? '';
  }

  #updateIntroText() {
    const introEl = this.#popupQuery('[data-ref="intro-text"]');
    if (!introEl) return;

    const template = this.#strings.intro || '';
    const productTitle = this.dataset.productTitle || '';
    const size = this.#sizeLabel || '—';

    introEl.textContent = template
      .replace(/\[product\]/gi, productTitle)
      .replace(/\[size\]/gi, size);
  }

  #updateSuccessText(email) {
    const successEl = this.#popupQuery('[data-ref="success-text"]');
    if (!successEl) return;

    const template = this.#strings.success || '';
    successEl.innerHTML = template.replace(/\{\{\s*email\s*\}\}/gi, email);
  }

  #getFieldContainer(input) {
    return input.closest('.field') ?? input.closest('.form-checkbox');
  }

  #displayErrors() {
    const emailField = this.#popupQuery('[data-ref="email-field"]');
    const emailError = this.#popupQuery('[data-ref="email-error"]');
    const checkboxesError = this.#popupQuery('[data-ref="checkboxes-error"]');

    emailField?.classList.remove('field--error');
    if (emailError) emailError.hidden = true;
    if (checkboxesError) checkboxesError.hidden = true;

    this.#checkboxInputs.forEach((input) => {
      this.#getFieldContainer(input)?.classList.remove('field--error');
    });

    if (
      this.#emailInput &&
      this.#shouldShowFieldError(this.#emailInput) &&
      !this.#validator?.isFieldValid(this.#emailInput)
    ) {
      emailField?.classList.add('field--error');
      this.#emailInput.setAttribute('aria-invalid', 'true');

      if (emailError) {
        emailError.textContent = this.#emailInput.value.trim()
          ? this.#strings.emailInvalid
          : this.#strings.emailRequired;
        emailError.hidden = false;
      }
    } else if (this.#emailInput) {
      this.#emailInput.removeAttribute('aria-invalid');
    }

    let checkboxMessage = '';

    for (const input of this.#checkboxInputs) {
      if (!this.#shouldShowFieldError(input)) continue;

      if (!this.#validator?.isFieldValid(input)) {
        this.#getFieldContainer(input)?.classList.add('field--error');
        checkboxMessage = this.#strings.checkboxRequired;
        break;
      }
    }

    if (checkboxMessage && checkboxesError) {
      checkboxesError.textContent = checkboxMessage;
      checkboxesError.hidden = false;
    }
  }

  #clearErrors() {
    this.#validator?.clearErrors();
    this.#popupQuery('[data-ref="email-error"]')?.setAttribute('hidden', '');
    this.#popupQuery('[data-ref="checkboxes-error"]')?.setAttribute('hidden', '');
    this.#popupQuery('[data-ref="form-error"]')?.setAttribute('hidden', '');
  }

  #showFormError(message) {
    const formError = this.#popupQuery('[data-ref="form-error"]');
    if (!formError) return;

    formError.textContent = message;
    formError.hidden = false;
  }

  #showSuccess(email) {
    this.#updateSuccessText(email);
    this.#popupQuery('[data-ref="form-state"]')?.setAttribute('hidden', '');
    this.#popupQuery('[data-ref="success-state"]')?.removeAttribute('hidden');
  }

  #resetToForm() {
    this.#submissionId += 1;
    this.#isSubmitting = false;
    this.#newsletterHandler?.abort();
    this.#form?.reset();
    this.#touchedFields.clear();
    if (this.#validator) {
      this.#validator.hasSubmittedOnce = false;
      this.#validator.clearErrors();
    }
    this.#clearErrors();
    this.#popupQuery('[data-ref="form-state"]')?.removeAttribute('hidden');
    this.#popupQuery('[data-ref="success-state"]')?.setAttribute('hidden', '');
    this.#setSubmitting(false);
  }

  #isFormComplete() {
    if (!this.#validator) return false;

    const fields = [this.#emailInput, ...this.#checkboxInputs].filter(Boolean);
    return fields.every((field) => this.#validator.isFieldValid(field));
  }

  // Per design the submit button stays enabled on an empty/invalid form; the
  // only disabled state is the in-flight request.
  #setSubmitting(isSubmitting) {
    const button = this.#popupQuery('[data-ref="submit-button"]');
    if (!button) return;

    button.disabled = isSubmitting;
    button.setAttribute('aria-disabled', isSubmitting ? 'true' : 'false');
    button.classList.toggle('is-loading', isSubmitting);
    button.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
  }
}

if (!customElements.get('pdp-notify-me')) {
  customElements.define('pdp-notify-me', PdpNotifyMe);
}
